import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Search, ExternalLink, Mail, Linkedin, X, Sparkles, Loader2, AlertTriangle, ShieldCheck, Building2, PenLine, Copy, Check, Download, Layers, Trash2, MoreVertical, Lightbulb, Bookmark, BookmarkPlus, Folder, Plus, Upload as UploadIcon, Ban, MailX } from "lucide-react";
import UploadLeadsModal from "@/components/UploadLeadsModal";

const STATUS_OPTIONS = [
  { v: "all", label: "All" },
  { v: "new", label: "New" },
  { v: "engaged", label: "Engaged" },
  { v: "replied", label: "Replied" },
  { v: "meeting", label: "Meeting" },
  { v: "archived", label: "Archived" },
];

function NewListInline({ onCreate, saving, err, name, setName }) {
  const [open, setOpen] = useState(false);
  const submit = async () => {
    const created = await onCreate(name);
    if (created) setOpen(false);
  };
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="px-3 py-1.5 rounded-full text-xs font-medium border border-dashed border-white/20 text-zinc-400 hover:text-emerald-300 hover:border-emerald-500/40 transition-colors flex items-center gap-1.5"
        data-testid="list-new-btn"
      >
        <Plus className="w-3.5 h-3.5" /> New list
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-1.5">
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") setOpen(false); }}
        placeholder="List name…"
        maxLength={60}
        className="input-glass py-1.5 px-3 text-xs w-40"
        data-testid="list-new-input"
      />
      <button onClick={submit} disabled={saving || !name.trim()} className="btn-primary text-xs py-1.5" data-testid="list-new-save">
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
      </button>
      <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white p-1" data-testid="list-new-cancel"><X className="w-3.5 h-3.5" /></button>
      {err && <span className="text-xs text-rose-300" data-testid="list-new-err">{err}</span>}
    </div>
  );
}

function ListPicker({ lists, currentIds, onToggle, onCreate, onClose, saving, anchor = "right" }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = React.useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    // Use mousedown so the click that opens the picker doesn't immediately close it
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);
  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    const created = await onCreate(name);
    setBusy(false);
    if (created) {
      setName("");
      onToggle(created.id, true);
    }
  };
  const anchorClass = anchor === "left" ? "left-0" : "right-0";
  return (
    <div ref={ref} className={`absolute ${anchorClass} top-full mt-2 w-64 glass-card p-3 z-[60] border border-white/10 shadow-2xl`} data-testid="list-picker">
      <div className="label-eyebrow mb-2">Save to list</div>
      <div className="max-h-56 overflow-y-auto space-y-1 mb-3">
        {lists.length === 0 && <div className="text-xs text-zinc-500 p-2">No lists yet. Create one below.</div>}
        {lists.map((lst) => {
          const included = currentIds.includes(lst.id);
          return (
            <label key={lst.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer" data-testid={`list-picker-item-${lst.id}`}>
              <input
                type="checkbox"
                checked={included}
                onChange={() => onToggle(lst.id, !included)}
                className="w-3.5 h-3.5 accent-emerald-400"
              />
              <Bookmark className="w-3.5 h-3.5 text-emerald-300 flex-shrink-0" />
              <span className="flex-1 text-sm text-zinc-200 truncate">{lst.name}</span>
              <span className="text-[10px] text-zinc-500 font-mono">{lst.count}</span>
            </label>
          );
        })}
      </div>
      <div className="border-t border-white/5 pt-3">
        <div className="flex items-center gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            placeholder="Create new list…"
            maxLength={60}
            className="input-glass py-1.5 px-3 text-xs flex-1"
            data-testid="list-picker-new-input"
          />
          <button onClick={submit} disabled={busy || saving || !name.trim()} className="btn-primary text-xs py-1.5" data-testid="list-picker-new-save">
            {(busy || saving) ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
          </button>
        </div>
      </div>
      <button onClick={onClose} className="mt-3 w-full text-xs text-zinc-500 hover:text-white py-1" data-testid="list-picker-close">Done</button>
    </div>
  );
}

const statusBadge = (s) => {
  const map = {
    new: "bg-zinc-700/30 text-zinc-300 border-zinc-600/30",
    engaged: "bg-blue-500/10 text-blue-300 border-blue-500/30",
    replied: "bg-yellow-300/10 text-yellow-300 border-yellow-300/30",
    meeting: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    archived: "bg-zinc-800/40 text-zinc-500 border-zinc-700/30",
  };
  return map[s] || map.new;
};

export default function Leads() {
  const { user } = useAuth();
  const demoMode = !!user?.demo_mode;
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [mining, setMining] = useState(false);
  const [miningReal, setMiningReal] = useState(false);
  const [mineMsg, setMineMsg] = useState("");
  const [draft, setDraft] = useState(null); // { subject, body, disclaimer, company, lead_id }
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState("");
  const [copied, setCopied] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [batchDrafting, setBatchDrafting] = useState(false);
  const [batchResult, setBatchResult] = useState(null); // { drafts, errors, total, succeeded, failed }
  const [deleting, setDeleting] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [unsubBusy, setUnsubBusy] = useState(false);
  const [freshStartDismissed, setFreshStartDismissed] = useState(() => {
    try { return localStorage.getItem("mlc-fresh-start-dismissed") === "1"; } catch { return false; }
  });
  const dismissFreshStart = () => {
    setFreshStartDismissed(true);
    try { localStorage.setItem("mlc-fresh-start-dismissed", "1"); } catch (e) { /* ignore */ }
  };
  const [firstMineToast, setFirstMineToast] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [lists, setLists] = useState([]);
  const [activeListId, setActiveListId] = useState("all");
  const [listPickerFor, setListPickerFor] = useState(null); // 'drawer' | 'batch' | null
  const [newListName, setNewListName] = useState("");
  const [listSaving, setListSaving] = useState(false);
  const [listErr, setListErr] = useState("");

  const fetchLists = useCallback(async () => {
    try {
      const { data } = await api.get("/lead-lists");
      setLists(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchLists(); }, [fetchLists]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    const params = {};
    if (status !== "all") params.status = status;
    if (q) params.q = q;
    if (activeListId !== "all") params.list_id = activeListId;
    try {
      const { data } = await api.get("/leads", { params });
      setLeads(data);
    } catch (e) {
      setLoadError(formatApiErrorDetail(e.response?.data?.detail) || "Could not load leads. Please try again.");
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [status, q, activeListId]);

  useEffect(() => { fetchLeads(); }, [status, activeListId, fetchLeads]);
  useEffect(() => {
    const t = setTimeout(fetchLeads, 300);
    return () => clearTimeout(t);
  }, [q, fetchLeads]);

  const updateStatus = async (id, newStatus) => {
    await api.patch(`/leads/${id}`, { status: newStatus });
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status: newStatus } : l)));
    if (selected?.id === id) setSelected({ ...selected, status: newStatus });
  };

  const mineNewLeads = async () => {
    setMining(true);
    setMineMsg("");
    try {
      const { data } = await api.post("/leads/generate", { count: 8 });
      setMineMsg(`+${data.generated} fresh leads from your My Leads Convert agent`);
      await fetchLeads();
      setTimeout(() => setMineMsg(""), 4500);
    } catch (e) {
      const status = e.response?.status;
      const raw = e.response?.data?.detail;
      let msg;
      if (status === 429) {
        msg = typeof raw === "string" ? raw : "Slow down — try again in a few seconds.";
      } else if (status === 400 && typeof raw === "string") {
        msg = raw; // "Set your ICP first"
      } else if (status === 502 || status === 503) {
        msg = "Our AI service is temporarily unavailable. Please try again in a minute.";
      } else if (status === 504 || !e.response) {
        msg = "Mining timed out. Claude can take a while — give it 30s and try again.";
      } else if (typeof raw === "string" && !raw.includes("<") && raw.length < 200) {
        msg = raw;
      } else {
        msg = "Could not mine new leads right now. Please try again shortly.";
      }
      setMineMsg(msg);
      setTimeout(() => setMineMsg(""), 6000);
    } finally {
      setMining(false);
    }
  };

  const mineRealLeads = async () => {
    setMiningReal(true);
    setMineMsg("");
    // Capture whether this is the very first time (no real leads yet + never celebrated)
    const hadNoRealBefore = !leads.some((l) => l.real_source);
    let alreadyCelebrated = true;
    try { alreadyCelebrated = localStorage.getItem("mlc-first-mine-celebrated") === "1"; } catch { /* ignore */ }
    try {
      const { data } = await api.post("/leads/mine-real", { count: 10 });
      if (data.generated === 0) {
        setMineMsg(data.note || "No new real matches this round.");
      } else {
        setMineMsg(`+${data.generated} real nonprofits sourced from ProPublica 990 filings`);
        if (hadNoRealBefore && !alreadyCelebrated) {
          setFirstMineToast(true);
          try { localStorage.setItem("mlc-first-mine-celebrated", "1"); } catch { /* ignore */ }
          // Fire-and-forget server-side activation record — idempotent
          api.post("/telemetry/first-mine").catch(() => { /* ignore */ });
          setTimeout(() => setFirstMineToast(false), 8000);
        }
      }
      await fetchLeads();
      setTimeout(() => setMineMsg(""), 5500);
    } catch (e) {
      const status = e.response?.status;
      const raw = e.response?.data?.detail;
      let msg;
      if (status === 429) {
        msg = typeof raw === "string" ? raw : "Slow down — try again in a few seconds.";
      } else if (status === 400 && typeof raw === "string") {
        msg = raw;
      } else if (status === 404 && typeof raw === "string") {
        msg = raw;
      } else if (status === 502) {
        msg = "ProPublica is temporarily unavailable. Please try again in a minute.";
      } else if (typeof raw === "string" && raw.length < 200) {
        msg = raw;
      } else {
        msg = "Could not fetch real nonprofit leads. Try again shortly.";
      }
      setMineMsg(msg);
      setTimeout(() => setMineMsg(""), 6000);
    } finally {
      setMiningReal(false);
    }
  };

  const draftOutreachEmail = async (lead) => {
    if (!lead) return;
    setDrafting(true);
    setDraftError("");
    setDraft(null);
    setCopied(false);
    try {
      const { data } = await api.post(`/leads/${lead.id}/draft-outreach`);
      setDraft(data);
    } catch (e) {
      const raw = e.response?.data?.detail;
      const msg = typeof raw === "string" && raw.length < 300 ? raw : "Could not draft outreach email. Please try again.";
      setDraftError(msg);
    } finally {
      setDrafting(false);
    }
  };

  const copyDraftToClipboard = async () => {
    if (!draft) return;
    const text = `Subject: ${draft.subject}\n\n${draft.body}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setDraftError("Copy failed. Select the text manually.");
    }
  };

  const toggleSelect = (leadId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else if (next.size < 10) next.add(leadId);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const runBatchDraft = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBatchDrafting(true);
    setBatchResult(null);
    try {
      const { data } = await api.post("/leads/batch-draft", { lead_ids: ids });
      setBatchResult(data);
    } catch (e) {
      const raw = e.response?.data?.detail;
      const msg = typeof raw === "string" && raw.length < 300 ? raw : "Batch drafting failed. Try again.";
      setBatchResult({ drafts: [], errors: ids.map((id) => ({ lead_id: id, error: msg })), total: ids.length, succeeded: 0, failed: ids.length });
    } finally {
      setBatchDrafting(false);
    }
  };

  const exportDraftsCsv = async ({ scope, list_id, lead_ids } = {}) => {
    // scope: "all" | "list" | "selected"
    // - "all" honours the current filter view (list chip + status + search)
    // - "list" passes an explicit list_id (used when a list chip is active)
    // - "selected" passes explicit lead_ids (batch modal or selection bar)
    setExporting(true);
    try {
      let payload;
      if (scope === "selected" && lead_ids?.length) {
        payload = { scope: "selected", lead_ids };
      } else if (activeListId && activeListId !== "all") {
        payload = { scope: "list", list_id: list_id || activeListId };
      } else {
        payload = { scope: "all" };
      }
      const resp = await api.post("/leads/export-drafts", payload, { responseType: "blob" });
      // Try to pull filename from Content-Disposition, fall back to a sensible default
      let filename = `myleadsconvert-drafts-${new Date().toISOString().slice(0, 10)}.csv`;
      const disp = resp.headers?.["content-disposition"] || resp.headers?.["Content-Disposition"];
      const m = disp && /filename="?([^"]+)"?/.exec(disp);
      if (m) filename = m[1];
      const url = URL.createObjectURL(resp.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      const exportedCount = resp.headers?.["x-drafts-exported"] || resp.headers?.["X-Drafts-Exported"] || "";
      setMineMsg(exportedCount ? `Exported ${exportedCount} draft${exportedCount === "1" ? "" : "s"} to CSV` : "Drafts exported");
      setTimeout(() => setMineMsg(""), 3500);
      // Refresh so exported_at badges appear
      await fetchLeads();
    } catch (e) {
      // Blob-typed errors need to be read as text
      let msg = "Could not export drafts. Try again.";
      try {
        if (e.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          const parsed = JSON.parse(txt);
          if (parsed?.detail) msg = parsed.detail;
        } else if (e.response?.data?.detail) {
          msg = e.response.data.detail;
        }
      } catch { /* keep default */ }
      setMineMsg(msg);
      setTimeout(() => setMineMsg(""), 5000);
    } finally {
      setExporting(false);
    }
  };

  const toggleLeadUnsubscribe = async (lead, nextValue) => {
    if (!lead) return;
    setUnsubBusy(true);
    try {
      const { data } = await api.patch(`/leads/${lead.id}/unsubscribe`, { unsubscribed: nextValue });
      setSelected((s) => s && s.id === lead.id ? { ...s, unsubscribed: data.unsubscribed, unsubscribed_at: data.unsubscribed_at } : s);
      setLeads((prev) => prev.map((l) => l.id === lead.id ? { ...l, unsubscribed: data.unsubscribed, unsubscribed_at: data.unsubscribed_at } : l));
      setMineMsg(nextValue ? "Marked as unsubscribed — future exports will skip this lead" : "Unsubscribe flag removed");
      setTimeout(() => setMineMsg(""), 3500);
    } catch (e) {
      setMineMsg(e.response?.data?.detail || "Could not update unsubscribe flag.");
      setTimeout(() => setMineMsg(""), 4000);
    } finally {
      setUnsubBusy(false);
    }
  };

  const downloadBatchCsv = () => {
    if (!batchResult || batchResult.drafts.length === 0) return;
    const ids = batchResult.drafts.map((d) => d.lead_id);
    exportDraftsCsv({ scope: "selected", lead_ids: ids });
  };

  const saveBatchAsCampaign = async () => {
    if (!batchResult || batchResult.drafts.length === 0) return;
    const first = batchResult.drafts[0];
    const combinedBody = batchResult.drafts
      .map((d) => `--- ${d.company} ---\nSubject: ${d.subject}\n\n${d.body}`)
      .join("\n\n\n");
    try {
      await api.post("/campaigns", {
        name: `Real outreach batch — ${new Date().toLocaleDateString()}`,
        channel: "email",
        subject: first.subject,
        message: combinedBody,
        steps: batchResult.drafts.length,
      });
      setBatchResult({ ...batchResult, saved: true });
    } catch (e) {
      setBatchResult({ ...batchResult, saveError: e.response?.data?.detail || "Could not save campaign." });
    }
  };

  const deleteSingleLead = async (lead) => {
    if (!lead) return;
    if (!window.confirm(`Delete "${lead.company || lead.name}" from your workspace? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/leads/${lead.id}`);
      setLeads((prev) => prev.filter((l) => l.id !== lead.id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(lead.id);
        return next;
      });
      setSelected(null);
      setMineMsg(`Deleted 1 lead`);
      setTimeout(() => setMineMsg(""), 3000);
    } catch (e) {
      const raw = e.response?.data?.detail;
      setMineMsg(typeof raw === "string" ? raw : "Could not delete lead.");
      setTimeout(() => setMineMsg(""), 4000);
    } finally {
      setDeleting(false);
    }
  };

  const deleteSelectedLeads = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected lead${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/leads/bulk-delete", { lead_ids: ids });
      setLeads((prev) => prev.filter((l) => !selectedIds.has(l.id)));
      clearSelection();
      setMineMsg(`Deleted ${data.deleted} lead${data.deleted === 1 ? "" : "s"}`);
      setTimeout(() => setMineMsg(""), 3000);
    } catch (e) {
      setMineMsg(e.response?.data?.detail || "Could not delete selected leads.");
      setTimeout(() => setMineMsg(""), 4000);
    } finally {
      setDeleting(false);
    }
  };

  const cleanupByScope = async (scope, label) => {
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    setCleanupBusy(true);
    setCleanupOpen(false);
    try {
      const { data } = await api.post("/leads/bulk-delete", { scope });
      await fetchLeads();
      clearSelection();
      setMineMsg(`Cleaned up ${data.deleted} lead${data.deleted === 1 ? "" : "s"} (${scope})`);
      setTimeout(() => setMineMsg(""), 4000);
    } catch (e) {
      setMineMsg(e.response?.data?.detail || "Cleanup failed.");
      setTimeout(() => setMineMsg(""), 4000);
    } finally {
      setCleanupBusy(false);
    }
  };

  const createList = async (name) => {
    const clean = (name || "").trim();
    if (!clean) return null;
    setListSaving(true);
    setListErr("");
    try {
      const { data } = await api.post("/lead-lists", { name: clean });
      setLists((prev) => [{ ...data }, ...prev]);
      setNewListName("");
      return data;
    } catch (e) {
      setListErr(e.response?.data?.detail || "Could not create list.");
      return null;
    } finally {
      setListSaving(false);
    }
  };

  const addLeadsToList = async (listId, leadIds, targetLabel = "list") => {
    if (!listId || leadIds.length === 0) return;
    try {
      await api.post(`/lead-lists/${listId}/leads`, { lead_ids: leadIds });
      await fetchLists();
      await fetchLeads();
      setMineMsg(`Added ${leadIds.length} lead${leadIds.length === 1 ? "" : "s"} to ${targetLabel}`);
      setTimeout(() => setMineMsg(""), 3500);
    } catch (e) {
      setMineMsg(e.response?.data?.detail || "Could not add to list.");
      setTimeout(() => setMineMsg(""), 4000);
    }
  };

  const removeLeadFromList = async (listId, leadId) => {
    try {
      await api.delete(`/lead-lists/${listId}/leads/${leadId}`);
      await fetchLists();
      await fetchLeads();
      // update selected lead in-memory
      setSelected((s) => s && s.id === leadId ? { ...s, list_ids: (s.list_ids || []).filter((x) => x !== listId) } : s);
    } catch (e) {
      setMineMsg(e.response?.data?.detail || "Could not remove from list.");
      setTimeout(() => setMineMsg(""), 3500);
    }
  };

  const deleteList = async (listId, listName) => {
    if (!window.confirm(`Delete list "${listName}"? Leads inside are kept — only the list is removed.`)) return;
    try {
      await api.delete(`/lead-lists/${listId}`);
      if (activeListId === listId) setActiveListId("all");
      await fetchLists();
      await fetchLeads();
      setMineMsg(`Deleted list "${listName}"`);
      setTimeout(() => setMineMsg(""), 3500);
    } catch (e) {
      setMineMsg(e.response?.data?.detail || "Could not delete list.");
      setTimeout(() => setMineMsg(""), 3500);
    }
  };

  const counts = useMemo(() => {
    const c = { all: leads.length };
    leads.forEach((l) => { c[l.status] = (c[l.status] || 0) + 1; });
    return c;
  }, [leads]);

  const hasRealLeads = useMemo(() => leads.some((l) => l.real_source), [leads]);
  const hasAnyLeads = leads.length > 0;
  const showFreshStartTip = !loading && !freshStartDismissed && hasAnyLeads && !hasRealLeads;

  return (
    <div className="p-8 lg:p-12 max-w-7xl">
      {firstMineToast && (
        <div className="fixed top-6 right-6 z-[70] glass-card p-4 pr-3 border border-emerald-500/40 bg-emerald-500/[0.12] shadow-2xl max-w-sm flex items-start gap-3 animate-in slide-in-from-right" data-testid="first-mine-toast">
          <div className="text-2xl leading-none flex-shrink-0" aria-hidden>🎯</div>
          <div className="flex-1">
            <div className="text-sm font-medium text-white mb-1">Your first real nonprofit just landed</div>
            <div className="text-xs text-emerald-100/90 leading-relaxed">
              Sourced live from ProPublica 990 filings. Open the lead and try <span className="text-emerald-300 font-medium">Draft outreach email</span> next.
            </div>
          </div>
          <button
            onClick={() => setFirstMineToast(false)}
            className="text-zinc-400 hover:text-white flex-shrink-0 -mt-1 p-1"
            aria-label="Dismiss"
            data-testid="first-mine-toast-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="label-eyebrow mb-2">Pipeline</div>
          <h1 className="font-display text-4xl font-medium text-white tracking-tight">Leads</h1>
          <p className="text-zinc-400 mt-2">Every real nonprofit sourced from public 990 filings — plus any demo leads you've turned on for testing.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <div className="text-right">
            <div className="text-3xl font-display text-gold">{leads.length}</div>
            <div className="text-xs text-zinc-500">matching leads</div>
          </div>
          <button onClick={mineRealLeads} disabled={miningReal || mining} className="btn-primary text-sm" data-testid="mine-real-leads" title="Sources verified nonprofits from ProPublica IRS 990 filings">
            {miningReal ? <><Loader2 className="w-4 h-4 animate-spin" /> Sourcing...</> : <><ShieldCheck className="w-4 h-4" /> Mine real leads</>}
          </button>
          <button
            onClick={() => setUploadOpen(true)}
            className="btn-ghost text-sm"
            data-testid="upload-leads-btn"
            title="Import contacts from a CSV or Excel spreadsheet"
          >
            <UploadIcon className="w-4 h-4" /> Upload CSV / Excel
          </button>
          <button
            onClick={() => exportDraftsCsv({})}
            disabled={exporting}
            className="btn-ghost text-sm"
            data-testid="export-drafts-btn"
            title={activeListId === "all" ? "Export every saved draft as a merge-ready CSV" : "Export saved drafts in the current list as a merge-ready CSV"}
          >
            {exporting ? <><Loader2 className="w-4 h-4 animate-spin" /> Exporting…</> : <><Download className="w-4 h-4" /> Export drafts (.csv)</>}
          </button>
          {demoMode && (
            <button onClick={mineNewLeads} disabled={mining || miningReal} className="btn-ghost text-sm" data-testid="mine-new-leads" title="Demo AI-generated leads for UI testing">
              {mining ? <><Loader2 className="w-4 h-4 animate-spin" /> Mining...</> : <><Sparkles className="w-4 h-4" /> Generate demo leads</>}
            </button>
          )}
          <div className="relative">
            <button
              onClick={() => setCleanupOpen((v) => !v)}
              disabled={cleanupBusy}
              className="btn-ghost text-sm"
              data-testid="cleanup-menu-btn"
              title="Bulk cleanup options"
            >
              {cleanupBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreVertical className="w-4 h-4" />} Clean up
            </button>
            {cleanupOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setCleanupOpen(false)} />
                <div className="absolute right-0 top-full mt-2 w-64 glass-card p-2 z-40 border border-white/10" data-testid="cleanup-menu">
                  <button
                    onClick={() => cleanupByScope("not_real", "all seed & demo data (keeping only real ProPublica leads)")}
                    className="w-full text-left px-3 py-2 rounded hover:bg-emerald-500/10 text-sm text-emerald-200 flex items-center gap-2"
                    data-testid="cleanup-not-real"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-emerald-300" /> Clear all seed data (keep real)
                  </button>
                  <div className="my-1 border-t border-white/5" />
                  <button
                    onClick={() => cleanupByScope("demo", "all AI demo leads")}
                    className="w-full text-left px-3 py-2 rounded hover:bg-white/5 text-sm text-zinc-200 flex items-center gap-2"
                    data-testid="cleanup-demo"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-amber-300" /> Delete all AI demo leads
                  </button>
                  <button
                    onClick={() => cleanupByScope("archived", "all archived leads")}
                    className="w-full text-left px-3 py-2 rounded hover:bg-white/5 text-sm text-zinc-200 flex items-center gap-2"
                    data-testid="cleanup-archived"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-zinc-400" /> Delete all archived leads
                  </button>
                  <div className="my-1 border-t border-white/5" />
                  <button
                    onClick={() => cleanupByScope("all", "EVERY lead in your workspace")}
                    className="w-full text-left px-3 py-2 rounded hover:bg-rose-500/10 text-sm text-rose-300 flex items-center gap-2"
                    data-testid="cleanup-all"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete all my leads
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="mb-6 glass-card p-4 border-emerald-400/30 bg-emerald-400/[0.05] flex items-start gap-3" data-testid="real-source-banner">
        <ShieldCheck className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 text-sm">
          <div className="font-medium text-white mb-1">Real nonprofit sourcing — ProPublica Nonprofit Explorer</div>
          <div className="text-zinc-300 leading-relaxed">
            "Mine real leads" pulls <span className="text-emerald-300 font-medium">verified US nonprofits</span> from IRS 990 filings via ProPublica. Each record includes the org's EIN, latest reported revenue, filing year, and NTEE code.
            <span className="block mt-1 text-zinc-400">Individual contact info (names, emails) is not fabricated — ProPublica exposes only the organization, so you'll need to research the right person to reach out to.</span>
          </div>
        </div>
      </div>

      {demoMode && (
        <div className="mb-6 glass-card p-4 border-amber-400/30 bg-amber-400/[0.06] flex items-start gap-3" data-testid="demo-mode-banner">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-medium text-white mb-1">Demo mode is ON — AI-generated sample leads visible</div>
            <div className="text-zinc-300 leading-relaxed">
              You're seeing <span className="text-amber-300 font-medium">plausible-sounding but fabricated</span> leads used for pitch and UI testing. Names, emails, LinkedIn URLs, and company domains may not correspond to real people.
              <span className="block mt-1 text-zinc-400">Turn off Demo mode in Settings to hide these controls.</span>
            </div>
          </div>
        </div>
      )}

      {mineMsg && (
        <div className="mb-5 glass-card p-3 px-4 text-sm text-gold border border-yellow-300/30 flex items-center gap-2" data-testid="mine-message">
          <Sparkles className="w-4 h-4" /> {mineMsg}
        </div>
      )}

      {showFreshStartTip && (
        <div className="mb-5 glass-card p-4 border-emerald-500/30 bg-emerald-500/[0.05] flex items-start gap-3" data-testid="fresh-start-tip">
          <Lightbulb className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-medium text-white mb-1">Not seeing real leads yet?</div>
            <div className="text-zinc-300 leading-relaxed">
              Your workspace is pre-loaded with sample data. Use{" "}
              <span className="text-emerald-300 font-medium">Clean up → Clear all seed data</span>{" "}
              then click{" "}
              <span className="text-emerald-300 font-medium">Mine real leads</span>{" "}
              to fill your workspace with only verified nonprofits from ProPublica.
            </div>
          </div>
          <button
            onClick={dismissFreshStart}
            className="text-zinc-500 hover:text-white flex-shrink-0 -mt-0.5 -mr-1 p-1"
            aria-label="Dismiss tip"
            data-testid="fresh-start-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {loadError && (
        <div className="mb-5 glass-card p-3 px-4 text-sm text-rose-300 border border-rose-500/30 flex items-center gap-2" data-testid="leads-load-error">
          <AlertTriangle className="w-4 h-4" /> {loadError}
        </div>
      )}

      {/* Toolbar */}
      <div className="glass-card p-4 mb-6 flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3.5 top-3 w-4 h-4 text-zinc-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, company, title, email..."
            className="input-glass pl-10"
            data-testid="leads-search"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.v}
              onClick={() => setStatus(opt.v)}
              data-testid={`filter-${opt.v}`}
              className={`px-3 py-2 rounded-full text-xs font-medium transition-all ${
                status === opt.v
                  ? "bg-gold text-black"
                  : "bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10 border border-white/10"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Lists filter chips */}
      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="lists-filter-row">
        <button
          onClick={() => setActiveListId("all")}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${
            activeListId === "all" ? "bg-emerald-500/20 text-emerald-200 border-emerald-500/40" : "bg-white/5 text-zinc-400 hover:text-white border-white/10 hover:border-white/20"
          }`}
          data-testid="list-filter-all"
        >
          <Folder className="w-3.5 h-3.5" /> All leads
        </button>
        {lists.map((lst) => (
          <div key={lst.id} className="group relative inline-flex">
            <button
              onClick={() => setActiveListId(lst.id)}
              className={`px-3 py-1.5 pr-2 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${
                activeListId === lst.id ? "bg-emerald-500/20 text-emerald-200 border-emerald-500/40" : "bg-white/5 text-zinc-300 hover:text-white border-white/10 hover:border-white/20"
              }`}
              data-testid={`list-filter-${lst.id}`}
            >
              <Bookmark className="w-3.5 h-3.5" />
              <span className="max-w-[160px] truncate">{lst.name}</span>
              <span className="text-[10px] text-zinc-500 font-mono">{lst.count}</span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); deleteList(lst.id, lst.name); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); deleteList(lst.id, lst.name); } }}
                className="ml-1 opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-rose-300 cursor-pointer inline-flex"
                data-testid={`list-delete-${lst.id}`}
                aria-label={`Delete list ${lst.name}`}
              >
                <X className="w-3 h-3" />
              </span>
            </button>
          </div>
        ))}
        <NewListInline onCreate={createList} saving={listSaving} err={listErr} name={newListName} setName={setNewListName} />
      </div>

      {/* Table */}
      <div className="glass-card overflow-hidden" data-testid="leads-table">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/5">
                <th className="px-4 py-4 w-8 text-xs font-semibold tracking-wider text-zinc-500 uppercase"></th>
                <th className="px-5 py-4 text-xs font-semibold tracking-wider text-zinc-500 uppercase">Lead</th>
                <th className="px-5 py-4 text-xs font-semibold tracking-wider text-zinc-500 uppercase">Company</th>
                <th className="px-5 py-4 text-xs font-semibold tracking-wider text-zinc-500 uppercase">Industry</th>
                <th className="px-5 py-4 text-xs font-semibold tracking-wider text-zinc-500 uppercase">Score</th>
                <th className="px-5 py-4 text-xs font-semibold tracking-wider text-zinc-500 uppercase">Source</th>
                <th className="px-5 py-4 text-xs font-semibold tracking-wider text-zinc-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-zinc-500">Loading...</td></tr>
              )}
              {!loading && leads.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-zinc-500">No leads match. Try widening your filter.</td></tr>
              )}
              {!loading && leads.map((l) => {
                const isChecked = selectedIds.has(l.id);
                return (
                  <tr key={l.id} onClick={() => setSelected(l)} className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer transition-colors" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleSelect(l.id)}
                        disabled={!isChecked && selectedIds.size >= 10}
                        className="w-4 h-4 accent-emerald-400 cursor-pointer"
                        data-testid={`select-lead-${l.id}`}
                        title={!isChecked && selectedIds.size >= 10 ? "Selection limit is 10 leads" : "Include in bulk action"}
                      />
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <div className="text-white text-sm font-medium">{l.name}</div>
                        {l.real_source ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 flex items-center gap-1" title="Real IRS-registered nonprofit from ProPublica"><ShieldCheck className="w-2.5 h-2.5" />Real</span>
                        ) : l.uploaded ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-300 border border-blue-500/30 flex items-center gap-1" title="Imported from a spreadsheet you uploaded"><UploadIcon className="w-2.5 h-2.5" />Uploaded</span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-400/10 text-amber-300 border border-amber-400/30 flex items-center gap-1" title={l.ai_generated ? "AI-generated demo lead — not a real person or org" : "Demo/seed lead — not verified against a real source"}><AlertTriangle className="w-2.5 h-2.5" />Demo</span>
                        )}
                        {l.unsubscribed && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-200 border border-amber-500/40 flex items-center gap-1" title="Skipped in CSV exports" data-testid={`lead-unsub-badge-${l.id}`}><MailX className="w-2.5 h-2.5" />Unsub</span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500">{l.title}</div>
                    </td>
                    <td className="px-5 py-4 text-sm text-zinc-300">{l.company}</td>
                    <td className="px-5 py-4 text-sm text-zinc-400">{l.industry}</td>
                    <td className="px-5 py-4">
                      <span className="font-mono text-sm text-gold">{l.score}</span>
                    </td>
                    <td className="px-5 py-4 text-xs text-zinc-500">{l.source}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs border ${statusBadge(l.status)} capitalize`}>{l.status}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selectedIds.size > 0 && !batchResult && (() => {
        const selectedLeads = leads.filter((l) => selectedIds.has(l.id));
        const realSelected = selectedLeads.filter((l) => l.real_source);
        return (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 glass-card p-3 pl-5 border border-emerald-500/30 bg-emerald-500/[0.08] shadow-2xl flex items-center gap-3 flex-wrap max-w-full" data-testid="batch-action-bar">
            <div className="flex items-center gap-2 text-sm text-white">
              <Layers className="w-4 h-4 text-emerald-300" />
              <span data-testid="batch-selection-count">{selectedIds.size}</span> {selectedIds.size === 1 ? "lead" : "leads"} selected
              {realSelected.length > 0 && <span className="text-xs text-emerald-300">({realSelected.length} real)</span>}
              <span className="text-xs text-zinc-500">(max 10)</span>
            </div>
            <button onClick={clearSelection} className="btn-ghost text-xs" data-testid="batch-clear">Clear</button>
            <button
              onClick={runBatchDraft}
              disabled={batchDrafting || realSelected.length === 0}
              className="btn-primary text-sm"
              data-testid="batch-draft-run"
              title={realSelected.length === 0 ? "Select at least one Real lead to draft outreach" : "Draft outreach openers for the selected real leads"}
            >
              {batchDrafting ? <><Loader2 className="w-4 h-4 animate-spin" /> Drafting {realSelected.length}…</> : <><PenLine className="w-4 h-4" /> Batch draft {realSelected.length > 0 ? `(${realSelected.length})` : ""}</>}
            </button>
            <button
              onClick={() => exportDraftsCsv({ scope: "selected", lead_ids: Array.from(selectedIds) })}
              disabled={exporting}
              className="px-3 py-1.5 rounded-full text-xs font-medium border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/10 transition-colors flex items-center gap-1.5"
              data-testid="batch-export-drafts"
              title="Export saved drafts on the selected leads as a merge-ready CSV"
            >
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Export drafts
            </button>
            <div className="relative">
              <button
                onClick={() => setListPickerFor(listPickerFor === "batch" ? null : "batch")}
                className="px-3 py-1.5 rounded-full text-xs font-medium border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/10 transition-colors flex items-center gap-1.5"
                data-testid="batch-add-to-list"
              >
                <BookmarkPlus className="w-3.5 h-3.5" /> Add to list
              </button>
              {listPickerFor === "batch" && (
                <ListPicker
                  lists={lists}
                  currentIds={[]}
                  onToggle={async (listId) => {
                    await addLeadsToList(listId, Array.from(selectedIds), lists.find((l) => l.id === listId)?.name || "list");
                    setListPickerFor(null);
                    clearSelection();
                  }}
                  onCreate={createList}
                  onClose={() => setListPickerFor(null)}
                  saving={listSaving}
                />
              )}
            </div>
            <button
              onClick={deleteSelectedLeads}
              disabled={deleting}
              className="px-3 py-1.5 rounded-full text-xs font-medium border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 transition-colors flex items-center gap-1.5"
              data-testid="batch-delete"
            >
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Delete {selectedIds.size}
            </button>
          </div>
        );
      })()}

      {/* Drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="lead-drawer">
          <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <div className="w-full max-w-md bg-zinc-950 border-l border-white/5 overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="label-eyebrow">Lead detail</div>
              <button onClick={() => setSelected(null)} className="text-zinc-400 hover:text-white" data-testid="close-drawer">
                <X className="w-5 h-5" />
              </button>
            </div>
            <h2 className="font-display text-3xl text-white mb-1">{selected.name}</h2>
            <div className="text-zinc-400">{selected.title} · {selected.company}</div>

            {selected.unsubscribed && (
              <div className="mt-4 p-3 rounded-lg bg-amber-500/[0.08] border border-amber-500/40 flex items-start gap-2" data-testid="drawer-unsubscribed-banner">
                <MailX className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-100 leading-relaxed">
                  This lead is flagged <span className="font-medium">unsubscribed</span>{selected.unsubscribed_at ? ` on ${new Date(selected.unsubscribed_at).toLocaleDateString()}` : ""}. Future CSV exports will skip it. Undo below if this was a mistake.
                </div>
              </div>
            )}

            {selected.real_source ? (
              <div className="mt-5 p-3 rounded-lg bg-emerald-500/[0.08] border border-emerald-500/30 flex items-start gap-2" data-testid="drawer-real-source">
                <ShieldCheck className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-emerald-200 leading-relaxed">
                  Real IRS-registered nonprofit sourced from ProPublica 990 filings.
                  {selected.ein && <span className="block mt-1 text-zinc-400">EIN {selected.ein}{selected.tax_period_year ? ` · latest filing ${selected.tax_period_year}` : ""}{selected.ntee_code ? ` · NTEE ${selected.ntee_code}` : ""}</span>}
                  <span className="block mt-1 text-zinc-400">Contact person is not provided by the data source — research the executive director on the org's official site before outreach.</span>
                  {selected.official_url && (
                    <a href={selected.official_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-emerald-300 hover:underline" data-testid="drawer-official-link">
                      View ProPublica record <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ) : selected.uploaded ? (
              <div className="mt-5 p-3 rounded-lg bg-blue-500/[0.08] border border-blue-500/30 flex items-start gap-2" data-testid="drawer-uploaded">
                <UploadIcon className="w-4 h-4 text-blue-300 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-200 leading-relaxed">
                  Imported from a spreadsheet you uploaded{selected.uploaded_at ? ` on ${new Date(selected.uploaded_at).toLocaleDateString()}` : ""}.
                  <span className="block mt-1 text-zinc-400">You are responsible for confirming permission/legitimate basis before contacting this lead (see Privacy Policy §2A).</span>
                </div>
              </div>
            ) : (
              <div className="mt-5 p-3 rounded-lg bg-amber-400/[0.08] border border-amber-400/30 flex items-start gap-2" data-testid="drawer-demo-warning">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-200 leading-relaxed">
                  Demo lead — this contact info was generated by AI and may not correspond to a real person or org. Do not send outreach.
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mt-6">
              <div className="glass-card p-4">
                <div className="label-eyebrow mb-1">Fit score</div>
                <div className="font-display text-2xl text-gold">{selected.score}</div>
              </div>
              <div className="glass-card p-4">
                <div className="label-eyebrow mb-1">Source</div>
                <div className="text-sm text-white">{selected.source}</div>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              {selected.email ? (
                <div className="flex items-start justify-between gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/5">
                  <div className="min-w-0">
                    <div className="label-eyebrow mb-1">Email</div>
                    <div className="text-sm text-white truncate">{selected.email}</div>
                  </div>
                  <a href={`mailto:${selected.email}`} className="text-zinc-400 hover:text-gold"><Mail className="w-4 h-4" /></a>
                </div>
              ) : (
                <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                  <div className="label-eyebrow mb-1">Email</div>
                  <div className="text-sm text-zinc-500 italic">Not provided by data source — research on org's official site</div>
                </div>
              )}
              {selected.linkedin && (
                <div className="flex items-start justify-between gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/5">
                  <div className="min-w-0">
                    <div className="label-eyebrow mb-1">{selected.real_source ? "Public record" : "LinkedIn"}</div>
                    <div className="text-sm text-white truncate">{selected.linkedin}</div>
                  </div>
                  <a href={selected.real_source ? selected.linkedin : `https://${selected.linkedin}`} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-gold">
                    {selected.real_source ? <Building2 className="w-4 h-4" /> : <Linkedin className="w-4 h-4" />}
                  </a>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                  <div className="label-eyebrow mb-1">Industry</div>
                  <div className="text-sm text-white">{selected.industry}</div>
                </div>
                <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                  <div className="label-eyebrow mb-1">Company size</div>
                  <div className="text-sm text-white">{selected.company_size}</div>
                </div>
                <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5 col-span-2">
                  <div className="label-eyebrow mb-1">Location</div>
                  <div className="text-sm text-white">{selected.location}</div>
                </div>
                {selected.real_source && selected.revenue_amount > 0 && (
                  <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5 col-span-2">
                    <div className="label-eyebrow mb-1">Latest 990 revenue{selected.tax_period_year ? ` (${selected.tax_period_year})` : ""}</div>
                    <div className="text-sm text-white">${selected.revenue_amount.toLocaleString()}</div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-8">
              <div className="label-eyebrow mb-3">Update status</div>
              <div className="flex flex-wrap gap-2">
                {["new", "engaged", "replied", "meeting", "archived"].map((s) => (
                  <button
                    key={s}
                    onClick={() => updateStatus(selected.id, s)}
                    data-testid={`set-status-${s}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all capitalize ${
                      selected.status === s
                        ? "bg-gold text-black border-gold"
                        : "bg-white/5 text-zinc-300 border-white/10 hover:border-gold/50"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <button
                    onClick={() => setListPickerFor(listPickerFor === "drawer" ? null : "drawer")}
                    className="px-3 py-1.5 rounded-full text-xs font-medium border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/10 transition-colors flex items-center gap-1.5"
                    data-testid="drawer-save-to-list"
                  >
                    <BookmarkPlus className="w-3.5 h-3.5" /> Save to list
                    {selected.list_ids && selected.list_ids.length > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/30 text-emerald-100">{selected.list_ids.length}</span>
                    )}
                  </button>
                  {listPickerFor === "drawer" && (
                    <ListPicker
                      lists={lists}
                      currentIds={selected.list_ids || []}
                      onToggle={async (listId, add) => {
                        if (add) {
                          await addLeadsToList(listId, [selected.id], lists.find((l) => l.id === listId)?.name || "list");
                          setSelected((s) => s ? { ...s, list_ids: [...(s.list_ids || []), listId] } : s);
                        } else {
                          await removeLeadFromList(listId, selected.id);
                        }
                      }}
                      onCreate={createList}
                      onClose={() => setListPickerFor(null)}
                      saving={listSaving}
                      anchor="left"
                    />
                  )}
                </div>
                <button
                  onClick={() => deleteSingleLead(selected)}
                  disabled={deleting}
                  className="px-3 py-1.5 rounded-full text-xs font-medium border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 transition-colors flex items-center gap-1.5"
                  data-testid="drawer-delete-lead"
                >
                  {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Delete this lead
                </button>
                <button
                  onClick={() => toggleLeadUnsubscribe(selected, !selected.unsubscribed)}
                  disabled={unsubBusy}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${
                    selected.unsubscribed
                      ? "border-amber-500/40 text-amber-200 bg-amber-500/[0.08] hover:bg-amber-500/[0.15]"
                      : "border-white/15 text-zinc-300 hover:border-amber-500/40 hover:text-amber-200"
                  }`}
                  data-testid="drawer-toggle-unsubscribe"
                  title={selected.unsubscribed ? "Undo unsubscribe — future exports will include this lead again" : "Skip this lead in every future CSV export"}
                >
                  {unsubBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : selected.unsubscribed ? <><Check className="w-3.5 h-3.5" /> Unsubscribed</> : <><Ban className="w-3.5 h-3.5" /> Mark unsubscribed</>}
                </button>
              </div>
            </div>

            {selected.saved_drafts && selected.saved_drafts.length > 0 && (
              <div className="mt-8 p-4 rounded-lg bg-blue-500/[0.05] border border-blue-500/20" data-testid="drawer-saved-drafts">
                <div className="flex items-center gap-2 mb-3">
                  <PenLine className="w-4 h-4 text-blue-300" />
                  <span className="text-sm font-medium text-blue-200">Saved drafts ({selected.saved_drafts.length})</span>
                </div>
                <div className="space-y-3">
                  {selected.saved_drafts.map((d, i) => (
                    <div key={i} className="glass-card p-3" data-testid={`saved-draft-${i}`}>
                      {d.subject && (
                        <>
                          <div className="label-eyebrow mb-1">Subject</div>
                          <div className="text-sm text-white mb-2">{d.subject}</div>
                        </>
                      )}
                      {d.body && (
                        <>
                          <div className="label-eyebrow mb-1">Body</div>
                          <div className="text-xs text-zinc-200 whitespace-pre-wrap leading-relaxed">{d.body}</div>
                        </>
                      )}
                      <div className="mt-2 text-[10px] text-zinc-500">Source: {d.source || "upload"}{d.created_at ? ` · ${new Date(d.created_at).toLocaleDateString()}` : ""}</div>
                      {d.exported_at && (
                        <div className="mt-1 text-[10px] text-emerald-300 flex items-center gap-1" data-testid={`saved-draft-exported-${i}`}>
                          <Check className="w-3 h-3" /> Exported on {new Date(d.exported_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selected.real_source && (
              <div className="mt-8 p-4 rounded-lg bg-emerald-500/[0.05] border border-emerald-500/20" data-testid="draft-outreach-section">
                <div className="flex items-center gap-2 mb-2">
                  <PenLine className="w-4 h-4 text-emerald-300" />
                  <span className="text-sm font-medium text-emerald-200">Draft outreach email</span>
                </div>
                <div className="text-xs text-zinc-400 mb-3 leading-relaxed">
                  Uses Claude to draft a short, personalized opener grounded in {selected.company}'s public 990 filing (EIN, revenue year, NTEE category). No mission text or contact names are fabricated.
                </div>
                <button
                  onClick={() => draftOutreachEmail(selected)}
                  disabled={drafting}
                  className="btn-primary text-xs"
                  data-testid="draft-outreach-btn"
                >
                  {drafting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Drafting…</> : <><PenLine className="w-3.5 h-3.5" /> Draft outreach email</>}
                </button>
                {draftError && (
                  <div className="mt-3 p-2.5 rounded bg-rose-500/10 border border-rose-500/30 text-xs text-rose-300" data-testid="draft-outreach-error">{draftError}</div>
                )}
              </div>
            )}

            <div className="mt-8 p-4 rounded-lg bg-yellow-300/5 border border-yellow-300/10">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-gold">
                  {selected.ai_generated ? "Why My Leads Convert picked this lead" : "My Leads Convert insight"}
                </span>
              </div>
              <div className="text-sm text-zinc-300 leading-relaxed">
                {selected.rationale ||
                  "Their team just posted a \"Demand Gen Manager\" role. Open with that hook — it converted 3.1× better last week."}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Draft outreach modal */}
      {draft && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="draft-outreach-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setDraft(null)} />
          <div className="relative w-full max-w-2xl bg-zinc-950 border border-emerald-500/20 rounded-2xl shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-white/5 flex items-center justify-between">
              <div>
                <div className="label-eyebrow mb-1 flex items-center gap-2">
                  <PenLine className="w-3 h-3 text-emerald-300" />
                  Outreach draft
                </div>
                <div className="font-display text-lg text-white">To: {draft.company}</div>
              </div>
              <button onClick={() => setDraft(null)} className="text-zinc-400 hover:text-white" data-testid="draft-outreach-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <div className="label-eyebrow mb-1.5">Subject</div>
                <div className="input-glass" data-testid="draft-outreach-subject">{draft.subject}</div>
              </div>
              <div>
                <div className="label-eyebrow mb-1.5">Body</div>
                <div className="glass-card p-4 whitespace-pre-wrap text-sm text-zinc-100 leading-relaxed" data-testid="draft-outreach-body">
                  {draft.body}
                </div>
              </div>
              <div className="p-3 rounded-lg bg-amber-400/[0.06] border border-amber-400/20 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-200 leading-relaxed" data-testid="draft-outreach-disclaimer">
                  {draft.disclaimer}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-white/5 flex items-center justify-between gap-3">
              <div className="text-xs text-zinc-500">Grounded in ProPublica 990 data. Review before sending.</div>
              <button onClick={copyDraftToClipboard} className="btn-primary text-sm" data-testid="draft-outreach-copy">
                {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy subject + body</>}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Batch draft results modal */}
      {batchResult && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center p-4" data-testid="batch-draft-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => { setBatchResult(null); clearSelection(); }} />
          <div className="relative w-full max-w-3xl bg-zinc-950 border border-emerald-500/20 rounded-2xl shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-white/5 flex items-center justify-between">
              <div>
                <div className="label-eyebrow mb-1 flex items-center gap-2">
                  <Layers className="w-3 h-3 text-emerald-300" />
                  Batch outreach draft
                </div>
                <div className="font-display text-lg text-white">
                  <span data-testid="batch-succeeded">{batchResult.succeeded}</span> of {batchResult.total} drafts ready
                  {batchResult.failed > 0 && <span className="text-rose-300 text-sm ml-2">· {batchResult.failed} failed</span>}
                </div>
              </div>
              <button onClick={() => { setBatchResult(null); clearSelection(); }} className="text-zinc-400 hover:text-white" data-testid="batch-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 max-h-[60vh] overflow-y-auto space-y-3">
              {batchResult.drafts.map((d, i) => (
                <div key={d.lead_id} className="glass-card p-4" data-testid={`batch-draft-${i}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-300" />
                    <div className="text-sm font-medium text-white">{d.company}</div>
                  </div>
                  <div className="text-xs text-zinc-500 mb-1.5">Subject</div>
                  <div className="text-sm text-white mb-3">{d.subject}</div>
                  <div className="text-xs text-zinc-500 mb-1.5">Body</div>
                  <div className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">{d.body}</div>
                </div>
              ))}
              {batchResult.errors && batchResult.errors.length > 0 && (
                <div className="p-3 rounded-lg bg-rose-500/[0.06] border border-rose-500/20 text-xs text-rose-200" data-testid="batch-errors">
                  <div className="font-medium mb-1">{batchResult.errors.length} lead{batchResult.errors.length === 1 ? "" : "s"} could not be drafted:</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {batchResult.errors.map((e) => (
                      <li key={e.lead_id}>{e.error}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="p-3 rounded-lg bg-amber-400/[0.06] border border-amber-400/20 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-200 leading-relaxed">
                  All drafts are grounded only in ProPublica 990 data. No recipient names, mission text, or programs were fabricated. Review each before sending.
                </div>
              </div>
              {batchResult.saved && (
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-200" data-testid="batch-saved">
                  Saved as a new campaign. Open the Campaigns page to review and launch.
                </div>
              )}
              {batchResult.saveError && (
                <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-xs text-rose-200" data-testid="batch-save-error">{batchResult.saveError}</div>
              )}
            </div>

            <div className="p-6 border-t border-white/5 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs text-zinc-500">
                Powered by Claude · Sourced from ProPublica 990 filings
              </div>
              <div className="flex items-center gap-2">
                <button onClick={downloadBatchCsv} disabled={exporting || batchResult.drafts.length === 0} className="btn-ghost text-sm" data-testid="batch-download-csv">
                  {exporting ? <><Loader2 className="w-4 h-4 animate-spin" /> Exporting…</> : <><Download className="w-4 h-4" /> Export drafts (.csv)</>}
                </button>
                <button onClick={saveBatchAsCampaign} disabled={batchResult.drafts.length === 0 || batchResult.saved} className="btn-primary text-sm" data-testid="batch-save-campaign">
                  {batchResult.saved ? <><Check className="w-4 h-4" /> Saved</> : <><Sparkles className="w-4 h-4" /> Save as campaign</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload leads modal */}
      <UploadLeadsModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        lists={lists}
        onDone={async () => { await fetchLists(); await fetchLeads(); }}
      />
    </div>
  );
}
